import React, { useCallback, useEffect, useMemo } from 'react'
import Modal from './Modal/Modal'
import { v4 as uuidv4 } from 'uuid'
import { AnimatePresence } from 'framer-motion'
import { ModalContext } from './useModal'

type Props = {
  children: React.ReactNode
}

export type HTMLOptions = {
  maxWidth?: 600 | 800 | 1024 | 1440
}

type ModalContent = {
  key: string
  content: React.ReactNode
  htmlOptions?: HTMLOptions
}

const ModalHost = (props: Props): JSX.Element => {
  const { children } = props

  const [modalContent, setModalContent] = React.useState<Array<ModalContent>>([])

  const handleModal = useCallback(
    (_modalContent: React.ReactNode, htmlOptions?: HTMLOptions) => {
      setModalContent((prev) => [
        ...prev,
        { key: uuidv4(), content: _modalContent, htmlOptions },
      ])
    },
    [],
  )

  const isOpen = useMemo(() => modalContent.length > 0, [modalContent])

  useEffect(() => {
    if (isOpen) document.documentElement.style.overflow = 'hidden'
    else document.documentElement.style.overflow = 'unset'
  }, [isOpen])

  const closeModal = useCallback(() => {
    setModalContent((prev) => prev.slice(0, -1))
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeModal()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [closeModal])

  const closeAllModals = useCallback(() => {
    setModalContent([])
  }, [])

  const contextValue = useMemo(
    () => ({
      isOpen,
      handleModal,
      closeModal,
      closeAllModals,
    }),
    [isOpen, handleModal, closeModal, closeAllModals],
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <AnimatePresence>
        {modalContent.map((mc) => {
          return (
            <Modal
              key={mc.key}
              isOpen={true}
              layoutId={mc.key}
              htmlOptions={mc.htmlOptions}>
              {mc.content}
            </Modal>
          )
        })}
      </AnimatePresence>
    </ModalContext.Provider>
  )
}
type Context = {
  isOpen: boolean
  handleModal: (modalContent: React.ReactNode, htmlOptions?: HTMLOptions) => void
  closeModal: () => void
  closeAllModals: () => void
}

export default ModalHost
